import { arenaBoundsFor, bossBodyBoundsFor } from './balance'
import type { AttackDef, Boss } from './types'

export type KnightSpacing = 'spear' | 'old'
let variant: KnightSpacing = 'spear'
export function getKnightSpacing(): KnightSpacing { return variant }
export function setKnightSpacing(v: KnightSpacing): void { variant = v }
export function usesKnightSpearSpacing(boss: Pick<Boss, 'id'>): boolean {
  return boss.id === 'vigil-knight' && variant === 'spear'
}

export const KNIGHT_RETREAT_TRIGGER_PX = 80
export const KNIGHT_RETREAT_TARGET_PX = 165
export const KNIGHT_POST_ATTACK_RESPACE_TRIGGER_PX = 120
export const KNIGHT_CROSS_WALL_ROOM_PX = 60
export const KNIGHT_CROSS_TARGET_PX = 195 // 175 preferred + 20
// A retreat that the player keeps inside 80 px for this long ends in the backward cut.
export const KNIGHT_CROSS_PURSUIT_MS = 500

// The wall charge is available from the first room, before the first execute (pack 09-29 W-3:
// 「1部屋目から出す」). DEV ?knightWallCross=late restores the previous room-2-onward unlock.
let wallCrossEarly = true
export function getKnightWallChargeEarly(): boolean { return wallCrossEarly }
export function setKnightWallChargeEarly(v: boolean): void { wallCrossEarly = v }

// How the knight deals with a player who is too close, and what the backleap sweep is for.
//   'mix' (default, 2026-09-29 user「切り下がりは薙ぎと似ていてタイミングをずらされる技の役割」「壁際突進は壁際の時とか、
//     位置関係がマンネリ状態になった時に使ってほしい」): the knight never walks backwards and is never forced to leap.
//     The backleap sweep is a delayed version of the single sweep: from room 2, when the player is within its reach
//     (KNIGHT_LEAP_MIX_RANGE_PX), a picked single sweep becomes the backleap sweep with KNIGHT_LEAP_MIX_ODDS.
//     Moving the fight elsewhere is the wall charge's job: at the wall (as before) or when the knight has stayed
//     in one place too long (KNIGHT_STALE_*).
//   'leap' = DEV ?knightBackoff=leap, the 09-29 morning build: inside 80 px it always leaps, from the first room.
//   'walk' = DEV ?knightBackoff=walk, the 09-28 behaviour: walk back, 40% sweep, sweep from room 2.
export type KnightBackoff = 'mix' | 'leap' | 'walk'
let backoff: KnightBackoff = 'mix'
export function getKnightBackoff(): KnightBackoff { return backoff }
export function setKnightBackoff(v: KnightBackoff): void { backoff = v }

// 'mix': the backleap sweep's reach and how often a single sweep picked inside it becomes the backleap sweep
// (user choice「近い時の薙ぎの代わりにくじで」, about 1 in 3). The reach is lanceCross's own: since 2026-10-01 it
// lunges up to 90 px and sweeps with the sweep's own tip (110 px) before hopping back = 200 px, the same as the sweep
// (user「一回前に詰めてから薙ぎ払って下がる」). Briefly 105 px on 10-01 (the bare spear while hopping); before 09-30, 115 px.
export const KNIGHT_LEAP_MIX_RANGE_PX = 200
// The backleap sweep is only drawn from where its spear tip (lanceCross strike 110 px) reaches the player's centre:
// it sweeps on the spot (10-01 night), so from farther out it always missed (user 10-02「デフォルトで発動する距離感が悪い」).
export const KNIGHT_CROSS_MIX_RANGE_PX = 110

// What the close-range substitute is (2026-10-01 user「介錯の武器変形の時みたいにダメージのない攻撃で突き飛ばして距離を離すのはどう？」
// → 「弾ける・弾けば押されない」「既存の横構えの絵で試す」).
//   'shove' (default) = lanceShove: the knight braces the spear upright (own art lanceShove_windup since 10-01 夜「押す絵を作った方がいい」;
//     the first trial borrowed lanceCross_backPlant), steps in and shoves the player
//     KNIGHT_SHOVE_PUSH_PX away with no damage. A parry stops the push and is an ordinary parry (attacks.ts knockbackParryable).
//   'cross' = DEV ?knightClose=cross: only the backleap sweep (lanceCross), and only inside KNIGHT_CROSS_MIX_RANGE_PX.
//   'both' (default since 2026-10-02, user choice「近い時の技に戻す」): the shove, or — when the player is inside the backleap sweep's
//     spear tip (KNIGHT_CROSS_MIX_RANGE_PX) with floor behind the knight — the backleap sweep half the time.
//     (From 10-01 night until then the backleap sweep never came up in a normal fight.) 'shove' = DEV ?knightClose=shove.
export type KnightCloseAnswer = 'shove' | 'cross' | 'both'
let closeAnswer: KnightCloseAnswer = 'both'
export function getKnightCloseAnswer(): KnightCloseAnswer { return closeAnswer }
export function setKnightCloseAnswer(v: KnightCloseAnswer): void { closeAnswer = v }
export const KNIGHT_SHOVE_PUSH_PX = 140
// Where the shove's step-in stops: the shaft's front edge (lanceShove strike 70 px) reaches 20 px past the player's x.
export const KNIGHT_SHOVE_LUNGE_GAP_PX = 50
let leapOdds = 1 / 3
export function getKnightLeapOdds(): number { return leapOdds }
// 'mix': how many string picks in a row the knight may make with the player within KNIGHT_LEAP_MIX_RANGE_PX before
// the next one is forced to be the close-range answer (shove / backleap sweep), whatever string was drawn
// (user 10-02「戦闘のテンポと同じ場所で単調な戦いになりすぎないバランス」). The draw above only rides on the single sweep,
// so on its own the fight could stay in one spot for over a minute (combat sim, room 2–3, bots staying close: gaps between
// shove / backleap sweep / wall charge up to 150 s). With 4: median 15–20 s, longest about 28 s; base draw unchanged.
// 0 = off (DEV ?knightClosePity=0, the 10-02 morning build).
export const KNIGHT_CLOSE_PITY_PICKS = 4
let closePity = KNIGHT_CLOSE_PITY_PICKS
export function getKnightClosePity(): number { return closePity }
export function setKnightClosePity(v: number): void { closePity = Math.max(0, Math.floor(v)) }
export function setKnightLeapOdds(v: number): void { leapOdds = Math.max(0, Math.min(1, v)) }

// The last act (3rd room) is built from strings (user 10-02「3幕は割と忙しい感じにしたい…3連とか5連とか連が主体に」):
// the single thrust and the single sweep are drawn at this fraction of their weight there. The close-range answers
// (shove / backleap sweep) still come through the cap above. 1 = the earlier mix (DEV ?knightAct3Singles=1).
export const KNIGHT_FINAL_ACT_SINGLE_MUL = 0.2
let finalActSingleMul = KNIGHT_FINAL_ACT_SINGLE_MUL
export function setKnightFinalActSingleMul(v: number): void { finalActSingleMul = Math.max(0, v) }
// 「最後の幕」= executesTaken が roomCount-1 (既定の2幕=幕2・DEV ?knightActs=3 の3幕版=幕3・src/knight_acts.ts)。
export function knightStringWeightMul(stringId: string, executesTaken: number, roomCount = 3): number {
  if (executesTaken < Math.max(1, roomCount) - 1) return 1
  return stringId === 'knight-proclamation' || stringId === 'knight-ward-sweep' ? finalActSingleMul : 1
}

// 'mix': the stalemate charge (user choice「騎士が動いていない時間」). When the knight has stayed inside a
// KNIGHT_STALE_BAND_PX band for KNIGHT_STALE_MS, its next attack is the wall charge, as long as the charge
// has at least KNIGHT_STALE_RUN_ROOM_PX of floor past the player to run through. DEV ?knightStaleCharge=off.
export const KNIGHT_STALE_MS = 10000
export const KNIGHT_STALE_BAND_PX = 150
export const KNIGHT_STALE_RUN_ROOM_PX = 300
let staleRunRoomPx = KNIGHT_STALE_RUN_ROOM_PX
export function getKnightStaleRunRoom(): number { return staleRunRoomPx }
export function setKnightStaleRunRoom(v: number): void { staleRunRoomPx = Math.max(0, v) }
let staleCharge = true
export function getKnightStaleCharge(): boolean { return staleCharge }
export function setKnightStaleCharge(v: boolean): void { staleCharge = v }

// Follows the knight's position: the anchor moves (and the clock restarts) once the knight leaves the band.
export function trackKnightStale(boss: Pick<Boss, 'x' | 'knightStaleAnchorX' | 'knightStaleMs'>, dtMs: number):
  { knightStaleAnchorX: number; knightStaleMs: number } {
  const anchor = boss.knightStaleAnchorX
  if (anchor === undefined || Math.abs(boss.x - anchor) > KNIGHT_STALE_BAND_PX / 2) {
    return { knightStaleAnchorX: boss.x, knightStaleMs: 0 }
  }
  return { knightStaleAnchorX: anchor, knightStaleMs: (boss.knightStaleMs ?? 0) + dtMs }
}

// Floor the charge runs over past the player: from the player to where the charge stops at the far wall.
export function knightChargeRoomPastPlayer(x: number, playerX: number): number {
  const bounds = arenaBoundsFor('vigil-knight')
  return playerX < x ? playerX - (bounds.leftWall + 55) : (bounds.rightWall - 55) - playerX
}

// How the backleap sweep differs from the single sweep before the swing (user「振る瞬間まで見分けがつかないのは
// 理不尽…違いは必ず作る…ただし明らかに簡単な違いではなく…数回取り組まないとわからないように」). Both start the
// same wind-up at the same moment; the backleap holds it longer (release 0.9 s vs 0.7 s).
//   'sink' (default) = the sweep swells slightly (the knight's usual telegraph swell); the backleap does not
//     swell but settles 5 px lower on bent knees, ready to spring.
//   'deep' = the same with a 10 px sink (easier to see). 'none' = no difference (DEV reference only).
export type KnightLeapTell = 'sink' | 'deep' | 'none'
let leapTell: KnightLeapTell = 'sink'
export function getKnightLeapTell(): KnightLeapTell { return leapTell }
export function setKnightLeapTell(v: KnightLeapTell): void { leapTell = v }
// The moment the sweep's wind-up pose appears (lanceSweep keypose timeline: idle until 0.45 of 700 ms).
export const KNIGHT_SWEEP_WINDUP_ONSET_MS = 315

export function knightSpearLungeGap(atk: Pick<AttackDef, 'id'>): number | null {
  if (atk.id === 'lanceShove') return KNIGHT_SHOVE_LUNGE_GAP_PX
  if (atk.id !== 'lanceThrust' && atk.id !== 'lanceSweep') return null
  // Director: 「当たりを絵に合わせて伸ばす」 grows only the hit. Keep the landing point
  // tuned in 09-28 (old measured visible reach minus 10), independent of new art measurements.
  return atk.id === 'lanceSweep' ? 85.6 : 118.2
}

// 後ろの余地 = 騎士の体が実際に止まる壁 (体の壁 = 部屋の壁 − 25px・BOSS_BODY_WALL_INSET_PX・2026-10-08 ユーザー採用) まで。
// 跳びの距離 (knightLeapTotalPx) がこの値なので、跳びが壁の手前で途中止まりせず、着地で終わる。
export function knightRoomBehind(x: number, playerX: number): number {
  const bounds = bossBodyBoundsFor('vigil-knight')
  return playerX < x ? bounds.rightWall - x : x - bounds.leftWall
}

export function knightCrossRetreatCap(x: number, playerX: number): number {
  return Math.max(0, Math.min(knightRoomBehind(x, playerX), KNIGHT_CROSS_TARGET_PX - Math.abs(x - playerX)))
}
